import { BlobWriter, ZipWriter } from '@zip.js/zip.js'
import saveAs from 'file-saver'
import { generateUniqueFilename } from '../utils'

export type ArchiveProgressReport = {
  currentStep: string
  totalFileCount?: number
  currentFileIndex?: number
  loaded?: number
  total?: number
}

export default class Archiver {
  static CHUNK_SIZE_LIMIT = 3 * 1024 * 1024 // 3MB
  static init(
    fileNameLinkPairs: Array<[string, string, number?]>,
    reporter?: (report: ArchiveProgressReport) => void,
  ): Archiver {
    const bundler = new Archiver(fileNameLinkPairs, reporter)
    bundler.sendReport('Preparing files')
    bundler.setupCounts()
    bundler.setupZipWriter()

    return bundler
  }

  private zipWriter: ZipWriter<Blob>
  private totalExpected: number = 0
  private thresholds: number[] = []
  private loaded: number = 0
  private doneCount: number = 0
  private cancelled: boolean = false

  private constructor(
    public fileNameLinkPairs: Array<[string, string, number?]>,
    public reporter?: (report: ArchiveProgressReport) => void,
  ) {}

  private setupZipWriter() {
    this.zipWriter = new ZipWriter(new BlobWriter('application/zip'), { level: 0 })
  }

  private setupCounts() {
    // Generate total expected file size and mock threshold to report file index progress to avoid linearity in actual downloads.
    this.fileNameLinkPairs.forEach((file, i) => {
      this.totalExpected += file[2]
      if (!i) {
        this.thresholds.push(file[2])
      } else {
        this.thresholds.push(file[2] + this.thresholds[i - 1])
      }
    })
    // Only use the expected total size if every file reported it's size. Otherwise we would have an uneven download calculation
    this.totalExpected = this.totalExpected * +this.fileNameLinkPairs.every(file => file[2])
    if (!this.totalExpected) {
      console.warn('No file size provided. Unable to show progress')
    }
  }

  private sendReport(currentStep, rest?: Omit<ArchiveProgressReport, 'currentStep'>) {
    this.reporter &&
      this.reporter({
        currentStep,
        ...rest,
      })
  }

  private read(reader: ReadableStreamDefaultReader, ctrl: ReadableStreamDefaultController) {
    reader
      .read()
      .then(({ done, value }) => {
        if (this.cancelled) {
          throw Error('User cancelled')
        }

        if (done) {
          this.doneCount += 1
          if (this.doneCount === this.fileNameLinkPairs.length) this.sendReport('Generating ZIP')
          return ctrl.close()
        }
        this.loaded += value.byteLength
        const currentThreshold = this.thresholds.find(thresh => thresh >= this.loaded)
        const currentIndex = this.thresholds.indexOf(currentThreshold)
        const prevThreshold = this.thresholds[currentIndex - 1] || 0

        this.sendReport('Downloading Files', {
          currentFileIndex: currentIndex + 1,
          totalFileCount: this.fileNameLinkPairs.length,
          loaded: this.loaded - prevThreshold,
          total: currentThreshold - prevThreshold,
        })

        ctrl.enqueue(value)
        this.read(reader, ctrl)
      })
      .catch(error => {
        ctrl.error(error)
      })
  }

  private fetchAndStream(link: string) {
    return fetch(link).then(responsePrev => {
      if (!responsePrev.ok) {
        throw Error(responsePrev.status + ' ' + responsePrev.statusText)
      }

      if (!responsePrev.body) {
        throw Error('ReadableStream not yet supported in this browser.')
      }

      return new Response(
        new ReadableStream({
          start: controller => {
            this.read(responsePrev.body.getReader(), controller)
          },
        }),
      )
    })
  }

  bundleMe(): Promise<void> {
    if (!this.fileNameLinkPairs || !this.fileNameLinkPairs.length) {
      return Promise.resolve()
    }

    this.doneCount = 0
    const nameCounts = {}

    return Promise.all(
      this.fileNameLinkPairs.map(async ([filename, link]) => {
        this.loaded = 0
        const uniqueFilename = generateUniqueFilename(filename, nameCounts)
        const response = await this.fetchAndStream(link)
        if (!response.body) throw new Error('No stream for ' + filename)
        return this.zipWriter.add(uniqueFilename, response.body)
      }),
    )
      .then(() => this.zipWriter.close())
      .then(zipFile => saveAs(zipFile, `attachments-${new Date().toISOString().replace(/-|\./g, '_')}.zip`))
      .catch(console.error.bind(null, 'Archiver:'))
  }

  cancel() {
    this.cancelled = true
  }
}
